import { createContext, useContext, type ReactNode } from "react";

import type {
  Announcement,
  AnnouncementComment,
  BoostAction,
  CreateCommentPayload,
  EditCommentPayload,
  ReactionCounts,
  ReactionType,
} from "./types";

export interface AnnouncementReactionsResult {
  reaction_counts: ReactionCounts;
  user_reaction: ReactionType | null;
}

export interface AnnouncementService {
  fetchReactions: (
    announcementId: number,
    signal?: AbortSignal,
  ) => Promise<AnnouncementReactionsResult>;
  upsertReaction: (
    announcementId: number,
    reactionType: ReactionType,
  ) => Promise<void>;
  deleteReaction: (announcementId: number) => Promise<void>;

  fetchComments: (
    announcementId: number,
    signal?: AbortSignal,
  ) => Promise<AnnouncementComment[]>;
  createComment: (
    announcementId: number,
    payload: CreateCommentPayload,
  ) => Promise<AnnouncementComment>;
  editComment: (
    announcementId: number,
    commentId: number,
    payload: EditCommentPayload,
  ) => Promise<AnnouncementComment>;
  deleteComment: (
    announcementId: number,
    commentId: number,
  ) => Promise<void>;

  fetchActiveBoostedPost: (
    signal?: AbortSignal,
  ) => Promise<Announcement | null>;
  logBoostAction: (
    announcementId: number,
    action: BoostAction,
    url?: string,
  ) => Promise<void>;
}

export interface AnnouncementAuthState {
  isAuthenticated: boolean;
  currentUserId: number | null;
  isPaying: boolean;
  email: string | null;
  firstName: string | null;
  requestSignIn: () => void;
}

interface ContextValue {
  service: AnnouncementService;
  auth: AnnouncementAuthState;
}

const AnnouncementServiceContext = createContext<ContextValue | null>(null);

interface ProviderProps extends ContextValue {
  children: ReactNode;
}

export function AnnouncementServiceProvider({
  service,
  auth,
  children,
}: ProviderProps) {
  return (
    <AnnouncementServiceContext.Provider value={{ service, auth }}>
      {children}
    </AnnouncementServiceContext.Provider>
  );
}

export function useAnnouncementService(): AnnouncementService {
  const ctx = useContext(AnnouncementServiceContext);
  if (!ctx) {
    throw new Error(
      "useAnnouncementService must be used within AnnouncementServiceProvider",
    );
  }
  return ctx.service;
}

export function useAnnouncementAuth(): AnnouncementAuthState {
  const ctx = useContext(AnnouncementServiceContext);
  if (!ctx) {
    throw new Error(
      "useAnnouncementAuth must be used within AnnouncementServiceProvider",
    );
  }
  return ctx.auth;
}
